import { Box, Button, HStack } from '@chakra-ui/react'
import React from 'react'

import { NodeType } from '../converters/shared/reactFlowTypes'
import type { SwitchboardTranslateFn } from '../types'

/**
 * Node type definitions with labels
 * Used across NodePalette, SwitchboardEditPage, and other components
 */
export const NODE_TYPE_DEFINITIONS: ReadonlyArray<{
  type: NodeType
  label: string
}> = [
  { type: NodeType.Transfer, label: 'Transfer' },
  { type: NodeType.TransferAndContinue, label: 'Transfer & Continue' },
  { type: NodeType.Queue, label: 'Queue' },
  { type: NodeType.AutoAttendant, label: 'Auto Attendant' },
  { type: NodeType.ExternalTransfer, label: 'External Transfer' },
  { type: NodeType.LeaveVoicemail, label: 'Leave Voicemail' },
  { type: NodeType.Schedule, label: 'Schedule' },
  { type: NodeType.DayNight, label: 'Day/Night' },
  { type: NodeType.DialByName, label: 'Dial by Name' },
  { type: NodeType.SoundClip, label: 'Sound Clip' },
  { type: NodeType.Wait, label: 'Wait' },
  { type: NodeType.HangUp, label: 'Hang Up' },
  { type: NodeType.JumpToSwitchboard, label: 'Jump To Switchboard' },
  { type: NodeType.ConferenceRoom, label: 'Conference Room' },
  { type: NodeType.TransferAndRollover, label: 'Transfer & Rollover' },
  { type: NodeType.CallDistribution, label: 'Call Distribution' },
  { type: NodeType.ChangeCallerId, label: 'Change Caller ID' },
  { type: NodeType.Webhook, label: 'Webhook' },
  { type: NodeType.InteractiveWebhook, label: 'Interactive Webhook' },
  { type: NodeType.FaxDetect, label: 'Fax Detect' },
  { type: NodeType.SmsNotification, label: 'SMS Notification' },
] as const

/**
 * Get fallback (English) node label by type (used when t is not provided)
 */
function getNodeLabelFallback(type: NodeType): string {
  if (type === NodeType.AiAgentV1) {
    return 'AI Agent'
  }
  return (
    NODE_TYPE_DEFINITIONS.find((def) => def.type === type)?.label ||
    type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

/**
 * Get node label by type, optionally translated
 */
export const getNodeLabel = (
  type: NodeType,
  t?: SwitchboardTranslateFn,
): string => {
  const fallback = getNodeLabelFallback(type)
  if (!t) return fallback
  const key = `switchboard.nodeLabels.${type}`
  const out = t(key, { defaultMessage: fallback })
  return out ?? fallback
}

export interface NodePaletteProps {
  onAddNode: (nodeType: NodeType) => void
  disabled?: boolean
  /**
   * Mango internal / staff: when true, the AI Agent node is available in the palette.
   */
  isStaff?: boolean
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

export const NodePalette: React.FC<NodePaletteProps> = ({
  onAddNode,
  disabled = false,
  t,
  isStaff = false,
}) => {
  // Get available node types (including AI Agent for internal staff), sorted alphabetically by label
  const availableNodeTypes = React.useMemo(() => {
    const baseTypes = [...NODE_TYPE_DEFINITIONS]
    if (isStaff) {
      baseTypes.push({ type: NodeType.AiAgentV1, label: 'AI Agent' })
    }
    return baseTypes.sort((a, b) => a.label.localeCompare(b.label))
  }, [isStaff])

  return (
    <Box overflowX="auto" overflowY="hidden">
      <HStack gap={2} flexWrap="wrap">
        {availableNodeTypes.map(({ type }) => (
          <Button
            key={type}
            variant="outline"
            h="9"
            minW="9"
            px="3.5"
            py="0.5"
            gap="2"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border.default"
            bg="bg.default"
            color="fg.default"
            fontWeight="medium"
            fontSize="sm"
            onClick={() => onAddNode(type)}
            disabled={disabled}
            _hover={{
              bg: 'bg.muted',
            }}
          >
            {getNodeLabel(type, t)}
          </Button>
        ))}
      </HStack>
    </Box>
  )
}
